// components/CombineMediaTab.tsx
import React, { useState, ChangeEvent } from 'react';

interface CombineMediaTabProps {
  onProcess: (mediaFile: File, audioFile: File) => Promise<Blob | null>;
  isLoading: boolean;
  message: string;
}

const CombineMediaTab: React.FC<CombineMediaTabProps> = ({ onProcess, isLoading, message }) => {
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [outputLoaded, setOutputLoaded] = useState<boolean>(false);

  // メディアファイル（画像または動画）のハンドラ
  const handleMediaChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    setMediaFile(file);
    
    // プレビューURLの作成
    const url = URL.createObjectURL(file);
    setMediaPreview(url);
    
    // 既存の出力をクリア
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
      setOutputUrl(null);
      setOutputLoaded(false);
    }
  };

  // 音声ファイルのハンドラ
  const handleAudioChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    setAudioFile(file);
    
    // プレビューURLの作成
    const url = URL.createObjectURL(file);
    setAudioPreview(url);
    
    // 既存の出力をクリア
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
      setOutputUrl(null);
      setOutputLoaded(false);
    }
  };

  // 処理実行ハンドラ
  const handleProcess = async (): Promise<void> => {
    if (!mediaFile || !audioFile) {
      alert('メディア（画像/動画）と音声の両方を選択してください');
      return;
    }
    
    try {
      const outputBlob = await onProcess(mediaFile, audioFile);
      if (outputBlob) {
        // 既存のURLを解放
        if (outputUrl) {
          URL.revokeObjectURL(outputUrl);
        }
        
        // 新しい出力のURLを作成
        const url = URL.createObjectURL(outputBlob);
        setOutputUrl(url);
        setOutputLoaded(false);
      }
    } catch (error) {
      console.error('処理エラー:', error);
      alert(`処理中にエラーが発生しました: ${(error as Error).message}`);
    }
  };

  // プレビューのファイル種類を判定
  const isImageFile = (file: File | null): boolean => {
    if (!file) return false;
    return file.type.startsWith('image/');
  };
  
  return (
    <div className="tab-pane">
      <h2>画像/動画と音声を合成</h2>
      <p className="description">
        静止画または動画に音声/音楽を追加して新しい動画を作成します。
      </p>
      
      <div className="input-groups">
        <div className="input-group">
          <label>画像または動画:</label>
          <input 
            type="file" 
            onChange={handleMediaChange}
            accept="image/jpeg,image/png,image/gif,video/mp4,video/webm" 
            disabled={isLoading}
          />
          
          {mediaPreview && (
            <div className="preview-container">
              {isImageFile(mediaFile) ? (
                <img 
                  src={mediaPreview} 
                  alt="メディアプレビュー" 
                  className="preview-media"
                />
              ) : (
                <video 
                  src={mediaPreview} 
                  controls 
                  className="preview-media"
                />
              )}
              <div className="file-name">{mediaFile?.name}</div>
            </div>
          )}
        </div>
        
        <div className="input-group">
          <label>音声ファイル:</label>
          <input 
            type="file" 
            onChange={handleAudioChange}
            accept="audio/mp3,audio/wav,audio/mpeg" 
            disabled={isLoading}
          />
          
          {audioPreview && (
            <div className="preview-container">
              <audio 
                src={audioPreview} 
                controls 
                className="preview-audio"
              />
              <div className="file-name">{audioFile?.name}</div>
            </div>
          )}
        </div>
      </div>
      
      <div className="actions">
        <button 
          onClick={handleProcess} 
          disabled={isLoading || !mediaFile || !audioFile}
          className="process-btn"
        >
          {isLoading ? '処理中...' : '合成を実行'}
        </button>
        
        {message && <div className="message">{message}</div>}
      </div>
      
      {outputUrl && (
        <div className="output-container">
          <h3>出力結果:</h3>
          <div className="video-container">
            <video 
              src={outputUrl} 
              controls 
              className="output-video"
              onLoadedData={() => setOutputLoaded(true)}
            />
            {!outputLoaded && <div className="loading-indicator">読み込み中...</div>}
          </div>
          <div className="download-container">
            <a 
              href={outputUrl} 
              download="combined-media.mp4" 
              className="download-btn"
            >
              ダウンロード
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default CombineMediaTab;